import { BANK_TEAM_OPEN_STAGES } from "@hakili/contracts";
import { formatDate, formatMoney, t } from "@hakili/i18n";
import { BANK_FS_META, Button, Callout } from "@hakili/ui";
import { AlertTriangle, ChevronRight } from "lucide-react";

import { initialsOf } from "../shell/BankShell.js";
import { DASH, fill } from "./dossiers.js";
import type { FichierBanque } from "./dossiers.js";
import { lineStageWords } from "../stage-words.js";
import { GuardedAct } from "./guarded-act.js";
import type { TeamMemberView, TeamSelfView } from "../team-read.js";

/**
 * `DESIGN SPRINT` (D-1026-2), Équipe, frame 80: **the team as D2 draws it.** One table (Analyste / Disponibilité / Dossiers par étape /
 * Charge); for each member a row of what she carries and, under it, her toggle and her actions; the band of the scale, the legend.
 *
 * What D2 draws with no field behind it is not drawn here (the sprint's rule 3): the charge bar on a scale of 20 with its 14 (the record
 * names neither), the days-over-limit variant (the limits are « valeur à confirmer »), « Qualité du traitement » and « Lecture de Hakili »
 * (the pilotage's figures). Each is listed in the pack.
 */

const STAGE_SHADE: Readonly<Record<(typeof BANK_TEAM_OPEN_STAGES)[number], string>> = {
  nouveau: "bg-[color:var(--hk-stage-nouveau)]",
  revue: "bg-[color:var(--hk-stage-revue)]",
  validation: "bg-[color:var(--hk-stage-validation)]",
  offre: "bg-[color:var(--hk-stage-offre)]",
  comite: "bg-[color:var(--hk-stage-comite)]",
  signature: "bg-[color:var(--hk-stage-signature)]",
  conditions: "bg-[color:var(--hk-stage-conditions)]",
  retour: "bg-[color:var(--hk-stage-retour)]",
};

/** What a row's stage bar reads: an analyst's load and the chef's own (`self`) have the same shape. */
interface Load {
  readonly id: string;
  readonly stages: Readonly<Record<string, number>>;
}

const capital = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/** « 1 revue · 1 validation »: D2's counts, in its words, for the stages the member holds. */
const stageCounts = (member: Load): readonly (readonly [string, string])[] =>
  BANK_TEAM_OPEN_STAGES.filter((code) => (member.stages[code] ?? 0) > 0).map(
    (code) => [code, `${String(member.stages[code] ?? 0)} ${lineStageWords(code)}`] as const,
  );

/** Whole days from the day a file entered its stage to the DOOR'S day (never the browser's clock). */
const daysIn = (enteredAt: string, asOf: string): number => {
  const from = Date.parse(`${enteredAt.slice(0, 10)}T00:00:00Z`);
  const to = Date.parse(`${asOf}T00:00:00Z`);
  return Number.isNaN(from) || Number.isNaN(to) ? 0 : Math.max(0, (to - from) / 86_400_000);
};

/** D2's `dotchip` (`.chip`, 8 px dot): the tone is the bank theme's own pair; the retired is the `line` chip. */
const CHIP =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-[5px] text-[length:var(--hk-fs-meta)] font-semibold leading-none";
const TONE = {
  ok: "bg-[color:var(--hk-ok-bg)] text-[color:var(--hk-ok)]",
  warn: "bg-[color:var(--hk-warn-bg)] text-[color:var(--hk-warn)]",
  brand: "bg-[color:var(--hk-brand-tint)] text-[color:var(--hk-brand-strong)]",
  line: "border border-[color:var(--hk-line)] bg-transparent text-[color:var(--hk-text-2)]",
  /** D2's `.chip` with no variant (`chip("…", "info")` has no `.chip.info` rule, so the base rule draws it): the filled pale pair, no dot. */
  neutral: "bg-[color:var(--hk-neutral-bg)] text-[color:var(--hk-text)]",
} as const;

/** « 28 octobre », D2's form (the year is dropped, as in « Écartés »). */
const dayOnly = (iso: string): string => formatDate(new Date(iso)).replace(/\s\d{4}$/u, "");

function AvailabilityChip(props: { readonly member: TeamMemberView }): React.JSX.Element {
  const { member } = props;
  const retired = member.statut === "desactive";
  const invited = member.statut === "invitationEnAttente";
  // Designer's precedence (311cbfab): retired > invited > absent > available.
  const absent = !invited && member.disponibilite === "absent";
  const words = retired
    ? fill("bank.equipe.compteRetireLe", {
        date: member.deactivatedOn === null ? DASH : formatDate(new Date(member.deactivatedOn)),
      })
    : invited
      ? t("bank.equipe.invitationEnAttente")
      : absent
        ? fill("bank.equipe.indisponibleJusquau", {
            date: member.retourLe === null ? t("bank.equipe.sonRetour") : dayOnly(member.retourLe),
          })
        : t("bank.equipe.disponible");
  const tone = retired ? TONE.line : invited ? TONE.brand : absent ? TONE.warn : TONE.ok;
  return (
    <span
      data-testid={`equipe-chip-${member.id}`}
      data-kind="chip-label"
      className={`${CHIP} ${tone} shrink-0`}
    >
      {retired ? null : (
        <i
          className="h-3.5 w-2 shrink-0 bg-[radial-gradient(circle_at_center,currentColor_0_4px,transparent_4.5px)]"
          aria-hidden="true"
        />
      )}
      {words}
    </span>
  );
}

/** D2's stacked bar: a segment per stage held, its flex the count, in the stage's own shade; the words beside it carry the stage. */
function StageStack(props: { readonly member: Load }): React.JSX.Element {
  const counts = stageCounts(props.member);
  const words = counts.map(([, label]) => label);
  return (
    <div
      role="img"
      aria-label={words.length === 0 ? t("bank.equipe.aucunDossierOuvert") : words.join(", ")}
      data-testid={`equipe-pile-${props.member.id}`}
      className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full bg-[color:var(--hk-line-soft)]"
    >
      {BANK_TEAM_OPEN_STAGES.filter((code) => (props.member.stages[code] ?? 0) > 0).map((code) => (
        <i
          key={code}
          data-stage={code}
          className={`${STAGE_SHADE[code]} block min-w-2`}
          style={{ flex: props.member.stages[code] ?? 0 }}
        />
      ))}
    </div>
  );
}

const pointsWords = (weight: number): string =>
  weight > 1
    ? fill("bank.equipe.ptsN", { n: String(weight) })
    : fill("bank.equipe.ptUn", { n: String(weight) });

/** The unfolded portfolio, D2's line: client, reference and amount, the stage chip, the weight, the days in the stage, « Réaffecter ». */
function Portfolio(props: {
  readonly memberId: string;
  readonly files: readonly FichierBanque[];
  readonly weights: Readonly<Record<string, number>>;
  readonly asOf: string;
  readonly busy: boolean;
  readonly onReassign: (file: FichierBanque) => void;
  /** Absent for the chef's own portfolio: he is not a member, so « Transférer tout son portefeuille » is not his (D2 frame 145). */
  readonly onTransfer?: () => void;
}): React.JSX.Element {
  const mine = props.files.filter((one) => one.analystUserId === props.memberId && !one.archived);
  return (
    <div className="pt-1" data-testid={`equipe-portefeuille-${props.memberId}`}>
      <ul className="grid list-none gap-0 p-0">
        {mine.length === 0 ? (
          <li className="py-2.5 text-sm text-[color:var(--hk-text-2)]">
            {t("bank.equipe.aucunDossierMotif")}
          </li>
        ) : (
          mine.map((file) => (
            <li
              key={file.id}
              className="grid grid-cols-[minmax(0,1.6fr)_auto_auto_minmax(0,1fr)_auto] items-center gap-3.5 border-t border-[color:var(--hk-line-soft)] py-2.5 max-[1099px]:grid-cols-2"
              data-testid={`equipe-dossier-${file.id}`}
            >
              <span className="min-w-0">
                <b>{file.clientName ?? DASH}</b>
                <small className={`${BANK_FS_META} text-[color:var(--hk-text-2)]`} data-meta>
                  {" · "}
                  {file.reference}
                  {" · "}
                  {formatMoney(file.amountFinanced)}
                </small>
              </span>
              <span
                data-kind="chip-label"
                className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--hk-brand-tint)] px-2.5 py-1 text-[length:var(--hk-fs-meta)] font-medium text-[color:var(--hk-brand-strong)]"
              >
                <i
                  className="h-3.5 w-1.5 shrink-0 bg-[radial-gradient(circle_at_center,currentColor_0_3px,transparent_3.5px)]"
                  aria-hidden="true"
                />
                {capital(lineStageWords(file.stage))}
              </span>
              <span className="font-[family-name:var(--hk-mono)]">
                {pointsWords(props.weights[file.stage] ?? 0)}
              </span>
              <span className="text-[color:var(--hk-text-2)]">
                {fill("bank.equipe.jours", { n: String(daysIn(file.stageEnteredAt, props.asOf)) })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={props.busy}
                data-testid={`equipe-reaffecter-${file.id}`}
                onClick={() => {
                  props.onReassign(file);
                }}
              >
                {t("bank.equipe.reaffecter")}
              </Button>
            </li>
          ))
        )}
      </ul>
      {mine.length === 0 || props.onTransfer === undefined ? null : (
        <div className="mt-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={props.busy}
            data-testid={`equipe-transferer-${props.memberId}`}
            onClick={props.onTransfer}
          >
            {t("bank.equipe.transfererTout")}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * `CHEF-HELD-FILES` (D-1027-1; D2 frames 144 to 146): **the chef's own line**, after the analysts, from the team door's `self`. « {nom} (vous) »
 * and « Chef du crédit », the chip « Détient des dossiers » (never an availability chip: he is not a member), the stage bar and the charge like
 * any row. Under it only what is his: « Déplier mes dossiers » (his own files, « Réaffecter » on each) and « Voir mes N dossiers dans le
 * pipeline ». NO « Déclarer une absence », NO « Retirer de l'équipe », NO « Transférer tout son portefeuille ».
 */
function SelfRows(props: {
  readonly self: TeamSelfView;
  readonly share: (points: number) => number;
  readonly files: readonly FichierBanque[];
  readonly weights: Readonly<Record<string, number>>;
  readonly asOf: string;
  readonly busy: boolean;
  readonly gestures: boolean;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly onFiles: () => void;
  readonly onReassign: (file: FichierBanque) => void;
}): React.JSX.Element {
  const { self } = props;
  const load: Load = { id: self.id, stages: self.stages };
  const counts = stageCounts(load);
  const cell = "border-b border-[color:var(--hk-line-soft)] px-5 py-3 align-middle";
  return (
    <tbody data-testid={`equipe-ligne-${self.id}`}>
      <tr>
        <td className={cell}>
          <div className="flex items-center gap-3 whitespace-nowrap">
            <span
              aria-hidden="true"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[color:var(--hk-text)] text-[length:var(--hk-fs-body)] font-semibold text-white"
            >
              {initialsOf(self.name)}
            </span>
            <div>
              <b className="block font-semibold">{fill("bank.equipe.chefVous", { nom: self.name })}</b>
              <small className={`${BANK_FS_META} text-[color:var(--hk-text-2)]`}>
                {t("bank.equipe.roleChef")}
              </small>
            </div>
          </div>
        </td>
        <td className={cell}>
          <span
            data-testid={`equipe-detient-${self.id}`}
            data-kind="chip-label"
            className={`${CHIP} ${TONE.neutral} shrink-0`}
          >
            {t("bank.equipe.detientDossiers")}
          </span>
        </td>
        <td className={`${cell} min-w-[230px]`}>
          <StageStack member={load} />
          <p
            className="mt-1 text-[length:var(--hk-fs-body)] text-[color:var(--hk-text-2)]"
            data-testid={`equipe-etapes-${self.id}`}
          >
            {counts.map(([, label]) => label).join(" · ")}
          </p>
        </td>
        <td className={`${cell} text-right`}>
          <b className="font-[family-name:var(--hk-mono)] text-lg" data-testid={`equipe-points-${self.id}`}>
            {self.points}
          </b>
          <small className={`${BANK_FS_META} text-[color:var(--hk-text-2)]`} data-meta>
            {" "}
            {t("bank.equipe.pts")}
          </small>
          <div
            role="img"
            aria-label={fill(self.points <= 1 ? "bank.equipe.chargeAriaUn" : "bank.equipe.chargeAria", {
              n: String(self.points),
            })}
            className="mt-1.5 h-1.5 w-full overflow-hidden rounded bg-[color:var(--hk-line)]"
          >
            <i
              className="block h-full bg-[color:var(--hk-brand)]"
              style={
                { width: "var(--w)", "--w": `${String(props.share(self.points))}%` } as React.CSSProperties
              }
            />
          </div>
        </td>
      </tr>
      <tr>
        <td colSpan={4} className="bg-[color:var(--hk-surface-2)] px-4 pb-3.5 pt-0">
          <div className="flex items-center justify-between gap-3">
            <span>
              {props.gestures ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 py-2 text-[length:var(--hk-fs-body)] font-semibold text-[color:var(--hk-brand-strong)]"
                  data-testid={`equipe-deplier-${self.id}`}
                  aria-expanded={props.open}
                  onClick={props.onToggle}
                >
                  <ChevronRight
                    className={`h-3.5 w-3.5 transition-transform ${props.open ? "rotate-90" : ""}`}
                    aria-hidden="true"
                  />
                  {props.open ? t("bank.equipe.replierMes") : t("bank.equipe.deplierMes")}
                </button>
              ) : null}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={props.busy}
              data-testid={`equipe-voir-dossiers-${self.id}`}
              onClick={props.onFiles}
            >
              {self.openFiles === 1
                ? t("bank.equipe.voirMesUn")
                : fill("bank.equipe.voirMesN", { n: String(self.openFiles) })}
            </Button>
          </div>
          {props.gestures && props.open ? (
            <Portfolio
              memberId={self.id}
              files={props.files}
              weights={props.weights}
              asOf={props.asOf}
              busy={props.busy}
              onReassign={props.onReassign}
            />
          ) : null}
        </td>
      </tr>
    </tbody>
  );
}

export function TeamTable(props: {
  readonly members: readonly TeamMemberView[];
  /** `CHEF-HELD-FILES`: the caller's own entry (`GET /bank/team`, `self`), `null` when he holds nothing: then there is no line of his. */
  readonly self: TeamSelfView | null;
  readonly weights: Readonly<Record<string, number>>;
  readonly asOf: string;
  readonly busy: boolean;
  readonly refusal: { readonly userId: string; readonly message: string } | null;
  readonly files: readonly FichierBanque[];
  /** The three gestures' doors are handed (round 1): the unfold, « Réaffecter » and the transfer are drawn only then (D-1019-2). */
  readonly gestures: boolean;
  readonly unfolded: ReadonlySet<string>;
  readonly onToggle: (memberId: string) => void;
  readonly onFiles: (memberId: string) => void;
  readonly onBack: (memberId: string) => void;
  readonly onAbsence: (memberId: string) => void;
  readonly onRemove: (memberId: string) => void;
  readonly onTransfer: (member: TeamMemberView) => void;
  readonly onReassign: (file: FichierBanque) => void;
}): React.JSX.Element {
  const { weights } = props;
  const weightOf = (stage: string): string => String(weights[stage] ?? DASH);
  // The heaviest load of the people still on the team (a retired account carries none and draws no bar); never below 1, so the quotient is defined.
  const heaviest = Math.max(
    1,
    ...props.members.filter((one) => one.statut !== "desactive").map((one) => one.points),
    props.self?.points ?? 0,
  );
  const share = (points: number): number => (points * 100) / heaviest;
  const th =
    "border-y border-[color:var(--hk-line-soft)] bg-[color:var(--hk-surface-2)] px-5 py-2.5 text-left text-[length:var(--hk-fs-micro)] font-semibold uppercase tracking-[0.08em] text-[color:var(--hk-text-2)]";
  return (
    <>
      <div
        className="overflow-hidden rounded-2xl border border-[color:var(--hk-line)] bg-[color:var(--hk-surface)]"
        data-testid="equipe-charge"
      >
        <table
          className="w-full border-collapse text-[length:var(--hk-fs-read)]"
          aria-label={t("bank.equipe.chargeTitre")}
        >
          <thead>
            <tr>
              <th className={th}>{t("bank.equipe.colAnalyste")}</th>
              <th className={th}>{t("bank.equipe.colDisponibilite")}</th>
              <th className={th}>{t("bank.equipe.colEtapes")}</th>
              <th className={`${th} text-right`}>{t("bank.equipe.colCharge")}</th>
            </tr>
          </thead>
          {props.members.map((member) => {
            const retired = member.statut === "desactive";
            const invited = member.statut === "invitationEnAttente";
            const absent = !invited && member.disponibilite === "absent";
            const refusal =
              props.refusal !== null && props.refusal.userId === member.id ? props.refusal : null;
            const counts = stageCounts(member);
            const open = props.unfolded.has(member.id);
            const cell = "border-b border-[color:var(--hk-line-soft)] px-5 py-3 align-middle";
            return (
              <tbody key={member.id} data-testid={`equipe-ligne-${member.id}`}>
                <tr>
                  <td className={cell}>
                    <div className="flex items-center gap-3 whitespace-nowrap">
                      <span
                        aria-hidden="true"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[color:var(--hk-text)] text-[length:var(--hk-fs-body)] font-semibold text-white"
                      >
                        {initialsOf(member.fullName)}
                      </span>
                      <div>
                        <b className={`block font-semibold ${retired ? "text-gray-500" : ""}`}>
                          {member.fullName}
                        </b>
                        <small className={`${BANK_FS_META} text-[color:var(--hk-text-2)]`}>
                          {t("bank.equipe.roleAnalyste")}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td className={cell}>
                    <AvailabilityChip member={member} />
                  </td>
                  <td className={`${cell} min-w-[230px]`}>
                    {counts.length === 0 ? (
                      retired ? null : (
                        <span className="text-[color:var(--hk-text-2)]">
                          {t("bank.equipe.aucunDossierOuvert")}
                        </span>
                      )
                    ) : (
                      <>
                        <StageStack member={member} />
                        <p
                          className="mt-1 text-[length:var(--hk-fs-body)] text-[color:var(--hk-text-2)]"
                          data-testid={`equipe-etapes-${member.id}`}
                        >
                          {counts.map(([, label]) => label).join(" · ")}
                        </p>
                      </>
                    )}
                    <span className="sr-only" data-testid={`equipe-en-cours-${member.id}`}>
                      {member.openFiles}
                    </span>
                  </td>
                  <td className={`${cell} text-right`}>
                    <b
                      className="font-[family-name:var(--hk-mono)] text-lg"
                      data-testid={`equipe-points-${member.id}`}
                    >
                      {member.points}
                    </b>
                    <small className={`${BANK_FS_META} text-[color:var(--hk-text-2)]`} data-meta>
                      {" "}
                      {t("bank.equipe.pts")}
                    </small>
                    {retired ? null : (
                      // D2 (0188ac18): the charge as a RELATIVE bar, points ÷ the team's heaviest load, always the brand's indigo
                      // (no scale of 20, no amber, no days-over-limit line: D-1026-AA-1, C).
                      <div
                        role="img"
                        aria-label={fill(
                          member.points <= 1 ? "bank.equipe.chargeAriaUn" : "bank.equipe.chargeAria",
                          {
                            n: String(member.points),
                          },
                        )}
                        className="mt-1.5 h-1.5 w-full overflow-hidden rounded bg-[color:var(--hk-line)]"
                      >
                        <i
                          className="block h-full bg-[color:var(--hk-brand)]"
                          style={
                            {
                              width: "var(--w)",
                              "--w": `${String(share(member.points))}%`,
                            } as React.CSSProperties
                          }
                        />
                      </div>
                    )}
                  </td>
                </tr>
                {retired ? null : (
                  <tr>
                    <td colSpan={4} className="bg-[color:var(--hk-surface-2)] px-4 pb-3.5 pt-0">
                      <div className="flex items-center justify-between gap-3">
                        <span>
                          {props.gestures && !invited && member.openFiles > 0 ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 py-2 text-[length:var(--hk-fs-body)] font-semibold text-[color:var(--hk-brand-strong)]"
                              data-testid={`equipe-deplier-${member.id}`}
                              aria-expanded={open}
                              onClick={() => {
                                props.onToggle(member.id);
                              }}
                            >
                              <ChevronRight
                                className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`}
                                aria-hidden="true"
                              />
                              {open ? t("bank.equipe.replier") : t("bank.equipe.deplier")}
                            </button>
                          ) : null}
                        </span>
                        <div className="flex flex-wrap items-start justify-end gap-2">
                          {member.openFiles === 0 ? (
                            <>
                              <GuardedAct
                                quiet
                                small
                                wrap
                                testId={`equipe-voir-dossiers-${member.id}`}
                                reasonId={`equipe-voir-raison-${member.id}`}
                                reason={t("bank.equipe.aucunDossierMotif")}
                              >
                                {t("bank.equipe.voirZero")}
                              </GuardedAct>
                              {props.gestures ? (
                                <GuardedAct
                                  small
                                  wrap
                                  testId={`equipe-transferer-${member.id}`}
                                  reasonId={`equipe-transferer-raison-${member.id}`}
                                  reason={t("bank.equipe.aucunDossierMotif")}
                                >
                                  {t("bank.equipe.transfererTout")}
                                </GuardedAct>
                              ) : null}
                            </>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={props.busy}
                              data-testid={`equipe-voir-dossiers-${member.id}`}
                              onClick={() => {
                                props.onFiles(member.id);
                              }}
                            >
                              {member.openFiles === 1
                                ? t("bank.equipe.voirSonUn")
                                : fill("bank.equipe.voirSesN", { n: String(member.openFiles) })}
                            </Button>
                          )}
                          {invited ? null : absent ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={props.busy}
                              data-testid={`equipe-rendre-disponible-${member.id}`}
                              onClick={() => {
                                props.onBack(member.id);
                              }}
                            >
                              {t("bank.equipe.rendreDisponible")}
                            </Button>
                          ) : (
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={props.busy}
                              data-testid={`equipe-absence-${member.id}`}
                              onClick={() => {
                                props.onAbsence(member.id);
                              }}
                            >
                              {t("bank.equipe.declarerAbsence")}
                            </Button>
                          )}
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={props.busy}
                            data-testid={`equipe-retirer-${member.id}`}
                            onClick={() => {
                              props.onRemove(member.id);
                            }}
                          >
                            {t("bank.equipe.retirer")}
                          </Button>
                        </div>
                      </div>
                      {props.gestures && open ? (
                        <Portfolio
                          memberId={member.id}
                          files={props.files}
                          weights={weights}
                          asOf={props.asOf}
                          busy={props.busy}
                          onReassign={props.onReassign}
                          onTransfer={() => {
                            props.onTransfer(member);
                          }}
                        />
                      ) : null}
                      {refusal === null ? null : (
                        <Callout
                          tone="bad"
                          className="mt-3"
                          testId={`equipe-blocage-${member.id}`}
                          icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
                        >
                          {/* The door's own sentence, with the count it counted. */}
                          {refusal.message}
                          {props.gestures && member.openFiles > 0 ? (
                            <div className="mt-2">
                              <Button
                                variant="secondary"
                                size="sm"
                                data-testid={`equipe-blocage-transferer-${member.id}`}
                                onClick={() => {
                                  props.onTransfer(member);
                                }}
                              >
                                {t("bank.equipe.transfertRaccourci")}
                              </Button>
                            </div>
                          ) : null}
                        </Callout>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            );
          })}
          {props.self === null ? null : (
            <SelfRows
              self={props.self}
              share={share}
              files={props.files}
              weights={weights}
              asOf={props.asOf}
              busy={props.busy}
              gestures={props.gestures}
              open={props.unfolded.has(props.self.id)}
              onToggle={() => {
                props.onToggle(props.self?.id ?? "");
              }}
              onFiles={() => {
                props.onFiles(props.self?.id ?? "");
              }}
              onReassign={props.onReassign}
            />
          )}
        </table>
        <p
          className="border-t border-[color:var(--hk-line-soft)] bg-[color:var(--hk-surface-2)] px-5 py-3 text-sm text-[color:var(--hk-text-2)]"
          data-testid="equipe-echelle"
        >
          {fill("bank.equipe.chargeBande", {
            liste: (["nouveau", "revue", "validation", "offre", "comite", "signature", "conditions"] as const)
              .map((code) => `${lineStageWords(code)} ${weightOf(code)}`)
              .join(" · "),
          })}
          {props.self === null ? null : ` ${t("bank.equipe.bandeChef")}`}
        </p>
      </div>
      <ul
        className="mx-0.5 mb-6 mt-2.5 flex flex-wrap gap-x-3.5 gap-y-1"
        aria-label={t("bank.equipe.legendeNom")}
        data-testid="equipe-legende"
      >
        {BANK_TEAM_OPEN_STAGES.map((code) => (
          <li
            key={code}
            data-legende={code}
            className={`flex items-center ${BANK_FS_META} text-[color:var(--hk-text-2)]`}
          >
            <i
              data-swatch
              className={`${STAGE_SHADE[code]} mr-1.5 inline-block h-2.5 w-2.5 rounded-[3px]`}
              aria-hidden="true"
            />
            {capital(lineStageWords(code))}
          </li>
        ))}
      </ul>
    </>
  );
}
